import axios from 'axios';
import { auth } from '../firebase';

// Configuration for different environments
const baseURL = process.env.REACT_APP_API_URL

const PRODUCT_ENDPOINT = `${baseURL}product`;
export const PRODUCTS_ENDPOINT = `${baseURL}products`;
const RETRY_ENDPOINT = `${baseURL}retry_product`;
const ACCOUNT_ENDPOINT = `${baseURL}account`;
const BULK_UPLOAD_ENDPOINT = `${baseURL}bulkupload`;
const PRICING_ENDPOINT = `${baseURL}pricing`;
const SALES_HISTORY_ENDPOINT = `${baseURL}sales_history`;
const IMAGES_ENDPOINT = `${baseURL}images_only`;

const createApiInstance = (apiKey) => {
    return axios.create({
        baseURL: baseURL,
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': `${apiKey}`, // User-specific API key
        }
    });
};

export const get = async (url, apiKey, params = {}) => {
    const api = createApiInstance(apiKey);
    try {
        const response = await api.get(url, { params });
      return response.data;
    } catch (error) {
        console.error(`GET ${url} failed:`, error);
        throw error; // Re-throw the error for the caller to handle
    }
};

export const post = async (url, apiKey, data) => {
    const api = createApiInstance(apiKey);
    try {
        const response = await api.post(url, data);
        return response.data;
    } catch (error) {
        console.error(`POST ${url} failed:`, error);
        throw error;
    }
};

// Billing endpoints authenticate with the signed-in user's Firebase ID token, since a
// brand-new account has no API key until its first subscription.
export const billingPost = async (url, data) => {
    const idToken = await auth.currentUser?.getIdToken();
    const headers = { 'Content-Type': 'application/json' };
    if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
    }
    try {
        const response = await axios.post(url, data, { baseURL, headers });
        return response.data;
    } catch (error) {
        console.error(`POST ${url} failed:`, error);
        throw error;
    }
};

export const put = async (url, apiKey, data) => {
    const api = createApiInstance(apiKey);
    try {
        const response = await api.put(url, data);
        return response.data;
    } catch (error) {
        console.error(`PUT ${url} failed:`, error);
        throw error;
    }
};

export const del = async (url, apiKey) => {
    const api = createApiInstance(apiKey);
    try {
        const response = await api.delete(url);
        return response.data;
    } catch (error) {
        console.error(`DELETE ${url} failed:`, error);
        throw error;
    }
};

export const postProductToChannel = async (apiKey, productId, channel, channelSettings, useOriginalImages = false) => {
    const api = createApiInstance(apiKey);
    const payload = { channel, channel_settings: channelSettings, use_original_images: useOriginalImages };

    try {
        const response = await api.post(`${PRODUCT_ENDPOINT}/${productId}`, payload);
        return response.data;
    } catch (error) {
        console.error(`Error posting product to channel: ${error.message}`);
        throw error; // Re-throw the error for the caller to handle
    }
};

export const updateProductChannel = async (apiKey, productId, channel, channelSettings = {}, useOriginalImages = false) => {
    const api = createApiInstance(apiKey);
    const payload = { channel, channel_settings: channelSettings, use_original_images: useOriginalImages };
    try {
        const response = await api.put(`${PRODUCT_ENDPOINT}/${productId}/channel`, payload);
        return response.data;
    } catch (error) {
        console.error(`Error updating product channel: ${error.message}`);
        throw error;
    }
};

export const deleteProductFromChannel = async (apiKey, productId, channel) => {
    const api = createApiInstance(apiKey);
    try {
        const response = await api.delete(`${PRODUCT_ENDPOINT}/${productId}/channel`, { data: { channel } });
        return response.data;
    } catch (error) {
        console.error(`Error deleting product from channel: ${error.message}`);
        throw error;
    }
};

// Synchronous regenerate (INTEGRATION_SPEC.md "Regenerate title / description /
// price / categories-and-fields"). `field` is 'title' | 'description' | 'price' | 'all'.
// The backend persists the result itself; the response carries only what changed.
export const regenerateProduct = async (apiKey, productId, field) => {
    const api = createApiInstance(apiKey);
    const response = await api.post(`${PRODUCT_ENDPOINT}/${productId}/regenerate`, { field });
    return response.data;
};

export const retryProduct = async (apiKey, productId, type, info) => {
    const api = createApiInstance(apiKey);
    const payload = { retry: type, force_input: info };

    try {
        const response = await api.post(`${RETRY_ENDPOINT}/${productId}`, payload);
        return response.data;
    } catch (error) {
        console.error(`Error retrying product l: ${error.message}`);
        throw error; // Re-throw the error for the caller to handle
    }
};

export const addProductImages = async (apiKey, productId, images) => {
    const api = createApiInstance(apiKey);
    try {
        const response = await api.post(`${PRODUCT_ENDPOINT}/${productId}/images`, { images });
        return response.data;
    } catch (error) {
        console.error(`Error adding product images: ${error.message}`);
        throw error;
    }
};

export const createStripeCustomer = async (userId) => {
    try {
        const { customerId } = await billingPost('create-stripe-user', { userId });
        return customerId;
    } catch (error) {
        console.error(`Error creating stripe customer: ${error.message}`);
        throw error; // Re-throw the error for the caller to handle
    }
};


export const getSquareCatalog = async (apiKey, { limit = 10, cursor = null, query = '' } = {}) => {
    const api = createApiInstance(apiKey);
    try {
        const params = { limit };
        if (cursor) params.cursor = cursor;
        if (query) params.q = query;
        const response = await api.get(`${baseURL}square/catalog`, { params });
        return response.data;
    } catch (error) {
        console.error(`Error fetching Square catalog: ${error.message}`);
        throw error;
    }
};

export const importSquareCatalog = async (apiKey, itemIds = null, cursor = null) => {
    const api = createApiInstance(apiKey);
    try {
        const body = {};
        if (itemIds) body.item_ids = itemIds;
        if (cursor) body.cursor = cursor;
        const response = await api.post(`${baseURL}square/import`, body);
        return response.data;
    } catch (error) {
        console.error(`Error importing Square catalog: ${error.message}`);
        throw error;
    }
};

// Generic multi-channel catalog browse/import — works for any channel with a
// registered adapter on the backend (currently 'square' and 'ebay'; see
// core/catalog_import_utils.py). Adding a new channel there is all it takes
// for these to start working with it too.
export const getChannelCatalog = async (channel, apiKey, { limit = 10, cursor = null, query = '' } = {}) => {
    const api = createApiInstance(apiKey);
    try {
        const params = { limit };
        if (cursor) params.cursor = cursor;
        if (query) params.q = query;
        const response = await api.get(`${baseURL}catalog/${channel}`, { params });
        return response.data;
    } catch (error) {
        console.error(`Error fetching ${channel} catalog: ${error.message}`);
        throw error;
    }
};

export const importChannelCatalog = async (channel, apiKey, itemIds = null, cursor = null) => {
    const api = createApiInstance(apiKey);
    try {
        const body = {};
        if (itemIds) body.item_ids = itemIds;
        if (cursor) body.cursor = cursor;
        const response = await api.post(`${baseURL}catalog/${channel}/import`, body);
        return response.data;
    } catch (error) {
        console.error(`Error importing ${channel} catalog: ${error.message}`);
        throw error;
    }
};

export const getEbaySettingsOptions = async (apiKey) => {
    const api = createApiInstance(apiKey);
    try {
        const response = await api.get(`${baseURL}ebay/settings-options`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching eBay settings options: ${error.message}`);
        throw error;
    }
};

export const getEtsySettingsOptions = async (apiKey) => {
    const api = createApiInstance(apiKey);
    try {
        const response = await api.get(`${baseURL}etsy/settings-options`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching Etsy settings options: ${error.message}`);
        throw error;
    }
};

export const searchEtsyTaxonomy = async (apiKey, query) => {
    const api = createApiInstance(apiKey);
    try {
        const response = await api.get(`${baseURL}etsy/taxonomy-search`, { params: { q: query } });
        return response.data;
    } catch (error) {
        console.error(`Error searching Etsy taxonomy: ${error.message}`);
        throw error;
    }
};

export const linkEbaySkus = async (apiKey) => {
    const api = createApiInstance(apiKey);
    try {
        const response = await api.post(`${baseURL}ebay/link`, {});
        return response.data;
    } catch (error) {
        console.error(`Error linking eBay SKUs: ${error.message}`);
        throw error;
    }
};

export default createApiInstance;